import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { isPremium, isAdmin, isCanceledButActive, getSubscriptionEndDate, getDaysUntilSubscriptionEnd } from '../utils/userAccess';
import GearIcon from '../assets/icons/gear.svg';
import SadDogIcon from '../assets/icons/sad-dog.svg';
import FlashMessage from '../components/FlashMessage';
import * as stripeApi from '../api/stripe';
import * as authApi from '../api/auth';
import './Settings.css';
import { FRONTEND_URL } from '../config';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser, isLoading } = useAuth();
  const [flashMessage, setFlashMessage] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login?redirectTo=/settings');
    }
  }, [user, navigate, isLoading]);

  // Check for success or canceled checkout
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    if (params.get('success') === 'true') {
      const sessionId = params.get('session_id');
      
      // Store session_id in localStorage for future billing portal requests
      if (sessionId) {
        localStorage.setItem('stripe_session_id', sessionId);
      }
      
      // Refresh user data to get updated subscription status
      refreshUser();
      
      setFlashMessage({
        message: 'Welcome to Premium! Your subscription is now active.',
        type: 'success'
      });
      // Clean up URL
      params.delete('success');
      params.delete('session_id');
      const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    
    if (params.get('canceled') === 'true') {
      setFlashMessage({
        message: 'Checkout canceled. You can upgrade anytime!',
        type: 'error'
      });
      // Clean up URL
      params.delete('canceled');
      const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [refreshUser]);

  const handleUpgradeToPremium = async () => {
    setUpgradeLoading(true);
    try {
      const { url, session_id } = await stripeApi.createCheckoutSession({
        lookup_key: 'premium_monthly',
        success_url: `${FRONTEND_URL}/settings?success=true&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${FRONTEND_URL}/settings?canceled=true`,
      });
      
      // Store session_id for later use in billing portal
      if (session_id) {
        localStorage.setItem('stripe_session_id', session_id);
      }
      
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setFlashMessage({ 
        message: error instanceof Error ? error.message : 'Unable to start checkout. Please try again.', 
        type: 'error' 
      });
      setUpgradeLoading(false);
    }
  };

  const handleManageSubscription = async (skipModal = false) => {
    // If this is a cancel action and we haven't shown the modal, show it first
    if (!skipModal && !isCanceledButActive(user)) {
      setShowCancelModal(true);
      return;
    }
    
    setLoading(true);
    try {
      // Get session_id if available (stored after successful checkout)
      const storedSessionId = localStorage.getItem('stripe_session_id');
      
      const { url } = await stripeApi.createPortalSession({
        return_url: `${FRONTEND_URL}/settings`,
        ...(storedSessionId && { session_id: storedSessionId })
      });
      
      // Redirect to billing portal in same window
      window.location.href = url;
    } catch (error) {
      console.error('Error opening billing portal:', error);
      setFlashMessage({ 
        message: error instanceof Error ? error.message : 'Unable to open billing portal. Please try again.', 
        type: 'error' 
      });
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await authApi.deleteAccount();
      // Show success message
      setFlashMessage({ 
        message: 'Account deleted successfully. We hope to see you again!', 
        type: 'success' 
      });
      setShowDeleteModal(false);
      // Wait a moment for the flash message to be visible
      setTimeout(() => {
        // Clear auth state locally without calling logout API (token is already invalid)
        localStorage.removeItem('chowmedown_auth_token');
        // Redirect to home page - full page reload to reset all state
        window.location.href = '/';
      }, 1500);
    } catch (error) {
      console.error('Error deleting account:', error);
      setFlashMessage({ 
        message: error instanceof Error ? error.message : 'Unable to delete account. Please try again.', 
        type: 'error' 
      });
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="settings-page">
      {flashMessage && (
        <FlashMessage
          message={flashMessage.message}
          type={flashMessage.type}
          onClose={() => setFlashMessage(null)}
        />
      )}
      
      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <img 
              src={SadDogIcon} 
              alt="Sad dog" 
              style={{ 
                width: '80px', 
                height: '80px', 
                marginBottom: '1rem',
                animation: 'scaleIn 0.3s ease-out'
              }}
            />
            <h2 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.5rem', fontWeight: 700 }}>
              Thinking of canceling?
            </h2>
            <p style={{ margin: '0 0 0.25rem 0', color: '#666', fontSize: '1.05rem' }}>
              Your pup might miss those homemade meals 🐾
            </p>
            <p style={{ margin: '0 0 2rem 0', color: '#666', fontSize: '1.05rem' }}>
              You can cancel anytime, but we'd love to keep helping you.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button 
                className="btn-primary"
                onClick={() => setShowCancelModal(false)}
                style={{ padding: '0.75rem 2rem', fontSize: '1.05rem' }}
              >
                Keep Premium
              </button>
              <button 
                className="btn-secondary"
                onClick={() => {
                  setShowCancelModal(false);
                  handleManageSubscription(true);
                }}
                style={{ padding: '0.75rem 2rem', fontSize: '1.05rem' }}
              >
                Continue to Cancel
              </button>
            </div>
            <p style={{ margin: '1.5rem 0 0 0', color: '#999', fontSize: '0.9rem' }}>
              You can always rejoin anytime.
            </p>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <div style={{ 
              fontSize: '3rem', 
              marginBottom: '1rem',
              animation: 'scaleIn 0.3s ease-out'
            }}>⚠️</div>
            <h2 style={{ margin: '0 0 1rem 0', color: '#d9534f', fontSize: '1.5rem', fontWeight: 700 }}>
              Delete Account?
            </h2>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '1.05rem' }}>
              This action cannot be undone. All your data, including dog profiles and saved recipes, will be permanently deleted.
            </p>
            <p style={{ margin: '0 0 2rem 0', color: '#666', fontSize: '1.05rem' }}>
              If you have an active subscription, it will be canceled and access will continue until the end of your billing period.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button 
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
                style={{ padding: '0.75rem 2rem', fontSize: '1.05rem' }}
              >
                Cancel
              </button>
              <button 
                className="btn-secondary"
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                style={{ 
                  padding: '0.75rem 2rem', 
                  fontSize: '1.05rem',
                  background: '#d9534f',
                  color: 'white',
                  border: '2px solid #d9534f'
                }}
                onMouseEnter={(e) => {
                  if (!deleteLoading) {
                    e.currentTarget.style.background = '#c9302c';
                    e.currentTarget.style.borderColor = '#c9302c';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!deleteLoading) {
                    e.currentTarget.style.background = '#d9534f';
                    e.currentTarget.style.borderColor = '#d9534f';
                  }
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Yes, Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="settings-container">
        <h1 className="settings-title">
          <img src={GearIcon} alt="" style={{ width: '32px', height: '32px', marginRight: '12px', verticalAlign: 'middle' }} />
          Settings
        </h1>

        {/* Subscription Section */}
        <section className="settings-section">
          <h2 className="section-title">Subscription</h2>
          
          <div className="subscription-status">
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
              <strong>Current Plan:</strong> {isPremium(user) || isAdmin(user) ? (
                <span style={{ color: '#28a745', fontWeight: 600 }}> Premium</span>
              ) : (
                <span style={{ color: '#6c757d', fontWeight: 600 }}> Free</span>
              )}
            </p>
          </div>

          {!isPremium(user) && !isAdmin(user) && (
            <div className="upgrade-box">
              <h3 style={{ margin: '0 0 0.75rem 0', color: '#b24646', fontSize: '1.2rem' }}>
                Unlock Premium Features
              </h3>
              <ul style={{ margin: '0 0 1rem 0', paddingLeft: '1.5rem', color: '#7a3a3a' }}>
                <li>Feed your dog with confidence</li>
                <li>Full recipe library</li>
                <li>Add and manage dog profiles </li>
                <li>Unlimited portion calculations</li>
                <li>Personalized feeding guidance</li>
                <li>New recipes added regularly</li>
              </ul>
              <p style={{ margin: '0 0 1rem 0', color: '#7a3a3a', fontSize: '0.95rem' }}>
                Only <strong>$4.99/month</strong> - Cancel anytime
              </p>
              <button 
                className="btn-primary" 
                onClick={handleUpgradeToPremium}
                disabled={upgradeLoading}
                style={{ width: 'auto', padding: '0.75rem 2rem', fontSize: '1.05rem' }}
              >
                {upgradeLoading ? 'Loading...' : 'Upgrade to Premium'}
              </button>
            </div>
          )}

          {(isPremium(user) || isAdmin(user)) && (
            <div className="premium-box">
              <h3 style={{ margin: '0 0 0.75rem 0', color: '#004085', fontSize: '1.3rem', fontWeight: 700 }}>
                You're on Premium 🎉
              </h3>
              <p style={{ margin: '0 0 0.5rem 0', color: '#004085', fontSize: '1rem', fontWeight: 600 }}>
                {isCanceledButActive(user) ? 'Access active until end of period' : 'Unlimited access active'}
              </p>
              <p style={{ margin: '0 0 1rem 0', color: '#6c757d', fontSize: '0.9rem' }}>
                You're currently enjoying unlimited recipes and portion calculations
              </p>
              
              {isCanceledButActive(user) && getSubscriptionEndDate(user) && (
                <>
                  <p style={{ margin: '0 0 0.25rem 0', color: '#856404', fontSize: '1rem', fontWeight: 600 }}>
                    Subscription ending {getSubscriptionEndDate(user)}
                  </p>
                  {getDaysUntilSubscriptionEnd(user) !== null && (
                    <p style={{ margin: '0 0 1rem 0', color: '#856404', fontSize: '0.9rem' }}>
                      ({getDaysUntilSubscriptionEnd(user)} {getDaysUntilSubscriptionEnd(user) === 1 ? 'day' : 'days'} remaining)
                    </p>
                  )}
                  <p style={{ margin: '0 0 1.5rem 0', color: '#6c757d', fontSize: '0.9rem' }}>
                    Your subscription has been canceled. You can reactivate anytime before it ends.
                  </p>
                </>
              )}
              
              {!isCanceledButActive(user) && !isAdmin(user) && user.current_period_end && (
                <p style={{ margin: '0 0 1.5rem 0', color: '#6c757d', fontSize: '0.9rem' }}>
                  Next billing date: {new Date(user.current_period_end).toLocaleDateString('en-US', { 
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                </p>
              )}
              
              {!isCanceledButActive(user) && !isAdmin(user) && !user.current_period_end && (
                <p style={{ margin: '0 0 1.5rem 0', color: '#6c757d', fontSize: '0.9rem' }}>
                  Active subscription - Renews automatically each month
                </p>
              )}
              
              {isAdmin(user) && (
                <p style={{ margin: '0 0 1.5rem 0', color: '#6c757d', fontSize: '0.9rem' }}>
                  Admin access - No billing required
                </p>
              )}
              
              {!isAdmin(user) && (
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {isCanceledButActive(user) ? (
                    <button 
                      className="btn-secondary"
                      onClick={() => handleManageSubscription(true)}
                      disabled={loading}
                      style={{ padding: '0.75rem 2rem', fontSize: '1.05rem', borderColor: '#28a745', color: '#28a745' }}
                      onMouseEnter={(e) => {
                        if (!loading) {
                          e.currentTarget.style.background = '#28a745';
                          e.currentTarget.style.color = 'white';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading) {
                          e.currentTarget.style.background = '#fff';
                          e.currentTarget.style.color = '#28a745';
                        }
                      }}
                    >
                      Reactivate Subscription
                    </button>
                  ) : (
                    <>
                      <button 
                        className="btn-secondary"
                        onClick={() => handleManageSubscription(true)}
                        disabled={loading}
                        style={{ padding: '0.75rem 2rem', fontSize: '1.05rem' }}
                      >
                        {loading ? 'Loading...' : 'Manage Subscription'}
                      </button>
                      <button 
                        className="btn-secondary"
                        onClick={() => handleManageSubscription(false)}
                        disabled={loading}
                        style={{ padding: '0.75rem 2rem', fontSize: '1.05rem', borderColor: '#d9534f', color: '#d9534f' }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.background = '#d9534f';
                            e.currentTarget.style.color = 'white';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.currentTarget.style.background = '#fff';
                            e.currentTarget.style.color = '#d9534f';
                          }
                        }}
                      >
                        Cancel Subscription
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Delete Account Section */}
        <section className="settings-section" style={{ borderTop: '1px solid #e0e0e0', paddingTop: '2rem', marginTop: '2rem' }}>
          <h2 className="section-title" style={{ color: '#d9534f' }}>Danger Zone</h2>
          
          <div style={{ 
            padding: '1.5rem',
            background: '#fff5f5',
            border: '1px solid #ffcccc',
            borderRadius: '8px'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#d9534f', fontSize: '1.1rem', fontWeight: 700 }}>
              Delete Account
            </h3>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '0.95rem' }}>
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <p style={{ margin: '0 0 1rem 0', color: '#666', fontSize: '0.95rem' }}>
              If you have an active subscription, it will be canceled and access will continue until the end of your billing period.
            </p>
            <button 
              className="btn-secondary"
              onClick={() => setShowDeleteModal(true)}
              style={{ 
                padding: '0.75rem 2rem', 
                fontSize: '1rem',
                borderColor: '#d9534f',
                color: '#d9534f'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#d9534f';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = '#d9534f';
              }}
            >
              Delete My Account
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Settings;
